import moment from 'moment';

import { hasExpired } from '../cacheValidation';
import { createAppSelector } from '../createAppSelector';

const baseState = {
    error: undefined,
    success: false,
    working: false,
};

const initState = {
    default: {
        ...baseState,
    },
};

function buildBaseDuck(namespace, sliceNamespace, idSerializer) {
    const types = buildBaseTypes(namespace, sliceNamespace);
    const actions = buildBaseActions(types, idSerializer);
    const selectors = buildBaseSelectors(namespace, sliceNamespace, idSerializer);
    const handlerMap = buildBaseHandlerMap(types);
    const reducer = buildBaseMapReducer(handlerMap);

    return {
        actions,
        handlerMap,
        reducer,
        selectors,
        types,
    };
}

function buildBaseTypes(namespace, sliceNamespace) {
    if (sliceNamespace) {
        return {
            END_WORK: `${sliceNamespace}/${namespace}/END_WORK`,
            EXPIRE: `${sliceNamespace}/${namespace}/EXPIRE`,
            RESET: `${sliceNamespace}/${namespace}/RESET`,
            START_WORK: `${sliceNamespace}/${namespace}/START_WORK`,
        };
    }

    return {
        END_WORK: `${namespace}/END_WORK`,
        EXPIRE: `${namespace}/EXPIRE`,
        RESET: `${namespace}/RESET`,
        START_WORK: `${namespace}/START_WORK`,
    };
}

function buildBaseActions(baseTypes, idSerializer = (id) => id) {
    return {
        // args [id, meta, error] or [meta, error]
        endWork(...args) {
            if (args.length <= 2) {
                args.unshift(undefined);
            }
            let [id, meta, error] = args;
            id = idSerializer(id);

            return {
                error,
                meta,
                payload: id,
                type: baseTypes.END_WORK,
            };
        },
        expire(id) {
            id = idSerializer(id);

            return {
                payload: id,
                type: baseTypes.EXPIRE,
            };
        },
        reset(id) {
            id = idSerializer(id);

            return {
                payload: id,
                type: baseTypes.RESET,
            };
        },
        startWork(id) {
            id = idSerializer(id);
            return {
                payload: id,
                type: baseTypes.START_WORK,
            };
        },
    };
}

function buildBaseHandlerMap(baseTypes) {
    return {
        [baseTypes.START_WORK]: _startWork,
        [baseTypes.END_WORK]: _endWork,
        [baseTypes.RESET]: _reset,
        [baseTypes.EXPIRE]: _expire,
    };
}

function _pickField(obj, field) {
    return Object.entries(obj).reduce((accum, [k, v]) => ({
        ...accum,
        [k]: v[field],
    }), {});
}

function buildBaseSelectors(namespace, sliceNamespace, idSerializer = (id) => id) {
    function selectAllWorking(state) {
        if (sliceNamespace) {
            state = state[sliceNamespace];
        }

        return _pickField(state[namespace], 'working');
    }

    function selectAllError(state) {
        if (sliceNamespace) {
            state = state[sliceNamespace];
        }

        return _pickField(state[namespace], 'error');
    }

    function selectAllSuccess(state) {
        if (sliceNamespace) {
            state = state[sliceNamespace];
        }

        return _pickField(state[namespace], 'success');
    }

    function defaultSelector(state) {
        if (sliceNamespace) {
            state = state[sliceNamespace];
        }

        return state[namespace].default;
    }

    function idSelector(state, id = 'default') {
        if (sliceNamespace) {
            state = state[sliceNamespace];
        }

        id = typeof id === 'string'
            ? id
            : idSerializer(id);

        return state[namespace][id];
    }

    function errorSelector(subState = baseState) {
        return subState.error;
    }

    function workingSelector(subState = baseState) {
        return subState.working;
    }

    function successSelector(subState = baseState) {
        return subState.success;
    }

    return {
        defaultSelector,
        idSelector,
        selectAllError,
        selectAllSuccess,
        selectAllWorking,
        selectError: createAppSelector(
            [(state, id) => idSelector(state, id)],
            errorSelector,
        ),
        selectExpired(state, id) {
            const subState = idSelector(state, id) || baseState;
            return hasExpired(subState);
        },
        selectExpiresAt(state, id) {
            const subState = idSelector(state, id) || baseState;
            return subState.expiresAt;
        },
        selectSuccess: createAppSelector(
            [(state, id) => idSelector(state, id)],
            successSelector,
        ),
        selectWorking: createAppSelector(
            [(state, id) => idSelector(state, id)],
            workingSelector,
        ),
    };
}

function _startWork(state, action) {
    const {
        payload: id = 'default',
    } = action;

    return {
        ...state,
        [id]: {
            ...state[id] || baseState,
            working: true,
        },
    };
}

function _endWork(state, action) {
    const {
        error,
        meta,
        payload: id = 'default',
    } = action;

    return {
        ...state,
        [id]: {
            ...state.default,
            error,
            success: !error,
            ...meta,
            working: false,
        },
    };
}

function _reset(state, action) {
    const {
        payload: id = 'default',
    } = action;

    return {
        ...state,
        [id]: {
            ...baseState,
        },
    };
}

function _expire(state, action) {
    const {
        payload: id = 'default',
    } = action;

    return {
        ...state,
        [id]: {
            ...state[id] || baseState,
            expiresAt: moment().subtract(1),
        },
    };
}

function buildBaseMapReducer(handlers, inititalState = initState) {
    // eslint-disable-next-line default-param-last -- Redux reducer signature requires (state, action)
    return function mapReducer(state = inititalState, action) {
        if (Object.prototype.hasOwnProperty.call(handlers, action.type)) {
            const handler = handlers[action.type];
            return handler(state, action);
        }

        return state;
    };
}

function combineDucks(...ducks) {
    return ducks.reduce((obj, duck) => {
        return {
            operations: {
                ...obj.operations || {},
                ...duck.operations,
            },
            reducers: {
                ...obj.reducers || {},
                [duck.namespace]: duck.reducer,
            },
            selectors: {
                ...obj.selectors || {},
                ...duck.selectors,
            },
        };
    }, {});
}

export {
    buildBaseActions,
    buildBaseDuck,
    buildBaseHandlerMap,
    buildBaseMapReducer,
    buildBaseSelectors,
    buildBaseTypes,
    combineDucks,
};
